import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Menu,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Fade,
  Chip,
  Stack,
  Badge,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

// Clave de localStorage (debe coincidir con UseSocket / NotificationRing)
const LS_KEY = 'app_notifications_by_type';

function loadMap() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveMap(map) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(map));
    window.dispatchEvent(new CustomEvent('notifications_updated', { detail: { map } }));
  } catch (e) {
    console.error('No se pudo guardar notificaciones', e);
  }
}

function formatDateToYYYYMMDD(dateString) {
  const date = new Date(dateString); // Convierte el string a un objeto Date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses van de 0 a 11, se ajusta sumando 1
  const day = String(date.getDate()).padStart(2, '0'); // Asegura que el día tenga 2 dígitos
  return `${year}-${month}-${day}`;
}

// Deducción de campos (flexible respecto a la estructura recibida)
function extractFields(notification) {
  if (!notification || typeof notification !== 'object') return { title: 'Notificación', message: '' };
  const { title, message, msg, description, body, text, createdAt, date, timeStamp, timestamp, type } = notification;
  const resolvedMessage = message || msg || description || body || text || '';
  const ts = createdAt || date || timeStamp || timestamp; // soporte para varias variantes
  const { payload } = notification || {};
  return {
    title: title || type || 'Notificación',
    message: resolvedMessage,
    createdAt: ts,
    payload: payload
  };
}

function formatDate(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString();
  } catch { return ''; }
}

const icons = {
  recordatorio_de_vestidos_pendientes: {
    type: 'recordatorio_de_vestidos_pendientes',
    title: 'Recordatorio de vestidos pendientes',
    path: (payload) => {
      const formattedStart = formatDateToYYYYMMDD(payload.closestDate);
      const formattedEnd = formatDateToYYYYMMDD(payload.farthestDate);
      return `/inventory/pending-items/dresses?start=${formattedStart}&end=${formattedEnd}`;
    }
  },
  recordatorio_de_trajes_pendientes: {
    type: 'recordatorio_de_trajes_pendientes',
    title: 'Recordatorio de trajes pendientes',
    path: (payload) => {
      const formattedStart = formatDateToYYYYMMDD(payload.closestDate);
      const formattedEnd = formatDateToYYYYMMDD(payload.farthestDate);
      return `/inventory/pending-items/suits?start=${formattedStart}&end=${formattedEnd}`;
    }
  },
  today_birthdays_reminder: {
    type: 'today_birthdays_reminder',
    title: 'Recordatorio de cumpleaños',
    path: () => {
      const today = new Date();
      const formattedDate = formatDateToYYYYMMDD(today);
      return `/events/birthday-list?start=${formattedDate}&end=${formattedDate}`;
    }
  },
  recordatorio_de_cumpleaños_quincenal: {
    type: 'recordatorio_de_cumpleaños_quincenal',
    title: 'Recordatorio de cumpleaños quincenal',
    path: (payload) => {
      const formattedStart = formatDateToYYYYMMDD(payload.closestDate);
      const formattedEnd = formatDateToYYYYMMDD(payload.farthestDate);
      return `/events/birthday-list?start=${formattedStart}&end=${formattedEnd}`;
    }
  }
};

export default function HeaderNotificationMenu({ anchorEl, open, onClose }) {
  const [mapVersion, setMapVersion] = useState(0); // para forzar recarga si hubieran cambios externos

  const navigate = useNavigate();
  // Escuchar evento global para re-render cuando otro componente modifique el storage
  useEffect(() => {
    const handler = () => setMapVersion(v => v + 1);
    window.addEventListener('notifications_updated', handler);
    return () => window.removeEventListener('notifications_updated', handler);
  }, []);

  const notificationsEntries = useMemo(() => {
    const map = loadMap();
    const entries = Object.entries(map); // [key, data]
    // Ordenar por fecha si existe createdAt/fecha interna
    return entries.sort((a, b) => {
      const aTs = new Date(extractFields(a[1]).createdAt || 0).getTime();
      const bTs = new Date(extractFields(b[1]).createdAt || 0).getTime();
      return bTs - aTs; // más reciente primero
    });
  }, [mapVersion, open]);

  const handleClearAll = useCallback(() => {
    saveMap({});
  }, []);

  const handleRemoveOne = useCallback((key) => {
    const map = loadMap();
    if (map[key]) {
      delete map[key];
      saveMap(map);
    }
  }, []);

  const isEmpty = notificationsEntries.length === 0;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'hidden',
            // Degradado sutil de fondo
          background: '#fff',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          borderRadius: 3,
          minWidth: 340,
          maxWidth: 400,
          maxHeight: 480,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 0.2, display: 'flex', alignItems: 'center', gap: 1, color: '#000' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1 }}>Notificaciones</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {isEmpty ? 'Sin notificaciones nuevas' : 'Centro de notificaciones'}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
      
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 0.5 }} />

      {/* Lista */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 0.5 }}>
        {isEmpty && (
          <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No tienes notificaciones pendientes.
            </Typography>
          </Box>
        )}
        {!isEmpty && (
          <List dense disablePadding>
            {notificationsEntries.map(([key, data]) => {
              const f = extractFields(data);
              const iconData = icons[key];
              return (
                <React.Fragment key={key}>
                  <ListItemButton
                    onClick={() => {
                      // Cerrar primero el menú para evitar parpadeos y luego navegar
                      if (onClose) onClose();
                      // Remover notificación inmediatamente
                      handleRemoveOne(key);
                      // Navegar (si hay ruta) tras un pequeño delay para que el cierre del menú se procese
                      if (iconData?.path) {
                        setTimeout(() => navigate(iconData.path(f.payload)), 40);
                      }
                    }}
                    alignItems="flex-start"
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.35)',
                      backdropFilter: 'blur(2px)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: 'rgba(247,89,171,0.18)',
                        '& .notif-title': { color: '#e91e63' }
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <NotificationsActiveIcon
                        variant="rounded"
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg,#f759ab 0%, #e91e63 100%)',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                          borderRadius: '50%',
                          p: 1,
                          color: 'white',
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography className="notif-title" sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.2 }}>
                          {f.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {f.message && (
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
                              {f.message}
                            </Typography>
                          )}
                          {f.createdAt && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.6rem', color: '#e91e63', fontWeight: 500 }}>
                              {formatDate(f.createdAt)}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ pr: 4 }}
                    />
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleRemoveOne(key); }}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          color: 'rgba(0,0,0,0.45)',
                          '&:hover': { color: '#e91e63' }
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>

                  </ListItemButton>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Menu>
  );
}
